import React, { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { OWNER_AVATAR_SIZES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { sortByDisplayName } from '@/shared/lib/sort/sortByDisplayName';
import { highlightText } from '@/shared/lib/text';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import type { DomainItem } from '@/entities/domain';

import styles from './SubDomainsTab.module.css';

const TABLE_HEADER_HEIGHT = 55;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 55;

type SubDomainsTabProps = {
  subDomains: DomainItem[];
  isLoading?: boolean;
};

export const SubDomainsTab: React.FC<SubDomainsTabProps> = ({ subDomains, isLoading = false }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const filteredSubDomains = useMemo(() => {
    if (!searchQuery || searchQuery.length === 0) return subDomains;

    const lowerQuery = searchQuery.toLowerCase();
    const filtered = subDomains.filter(
      (domain) =>
        domain.name.toLowerCase().includes(lowerQuery) ||
        domain.fullyQualifiedName.toLowerCase().includes(lowerQuery) ||
        domain.displayName?.toLowerCase().includes(lowerQuery) ||
        domain.description?.toLowerCase().includes(lowerQuery)
    );
    return sortByDisplayName(filtered);
  }, [subDomains, searchQuery]);

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredSubDomains.length,
    viewportOffset: 648,
    rowHeight: TABLE_ROW_HEIGHT,
    headerHeight: TABLE_HEADER_HEIGHT,
    paginationHeight: TABLE_PAGINATION_HEIGHT,
  });

  const columns: ColumnsType<DomainItem> = useMemo(() => {
    const cols: ColumnsType<DomainItem> = [
      {
        title: '하위 도메인',
        dataIndex: 'name',
        key: 'name',
        width: 320,
        fixed: 'left',
        render: (_name: string, record: DomainItem) => {
          const label = record.displayName || record.name;
          return (
            <Link to={`/domain/${encodeURIComponent(record.fullyQualifiedName)}`}>
              {highlightText(label, searchQuery, { className: 'highlight' })}
            </Link>
          );
        },
        sorter: (a, b) => {
          const aLabel = a.displayName || a.name;
          const bLabel = b.displayName || b.name;
          return aLabel.localeCompare(bLabel, 'ko', { sensitivity: 'base', numeric: true });
        },
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        render: (description?: string) => {
          return <AssetDescription description={description} mode="expandable" highlightQuery={searchQuery.trim()} />;
        },
      },
    ];

    if (showOwners) {
      cols.push({
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: 200,
        render: (owners: DomainItem['owners']) => {
          if (!owners || owners.length === 0) return '-';
          return <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      });
    }

    return cols;
  }, [showOwners, searchQuery]);

  return (
    <div className={styles.subDomainsTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder="이 도메인 내에서 검색..."
          value={searchQuery}
          onChange={(e) => {
            const validatedValue = validateLength(e.target.value);
            setSearchQuery(validatedValue);
          }}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          className={styles.searchInput}
          allowClear
          maxLength={30}
        />
      </div>
      <div className={styles.contentArea}>
        <Table
          columns={columns}
          dataSource={filteredSubDomains}
          rowKey={(record) => record.id}
          loading={isLoading}
          pagination={paginationConfig}
          scroll={{ x: filteredSubDomains.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
          locale={{
            emptyText: (
              <TableEmpty
                searchQuery={searchQuery}
                description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
              />
            ),
          }}
          expandable={{
            childrenColumnName: '__children__',
          }}
        />
      </div>
    </div>
  );
};
